import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminFunctions from "./AdminFunctions";
import AuthFunctions from "../services/AuthService";
import Session from "../auth/Session";
import {
    Container,
    Row,
    Col,
    Table,
    Button,
    Input,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Badge,
    Offcanvas,
    OffcanvasHeader,
    OffcanvasBody,
} from "reactstrap";
import { BsFilter, BsChevronDown } from "react-icons/bs";
import Select from "react-select";

const emptyJob = {
    id: 0,
    customerName: "",
    jobName: "",
    approvedOn: null,
    soldDate: null,
    manHours: null,
    rep: "",
    jobClass: "",
    address: "",
    equipmentIds: [],
    equipmentNames: "",
    materialRequirements: "",
    schedulingNotes: "",
    isOpen: true,
    invoiceNumber: null,
    quickBooksJobId: null,
};

const canNavigate = ["Admin", "Office Admin", "Project Manager"];

const repOptions = [
    { value: "CG", label: "CG" },
    { value: "JWG", label: "JWG" },
];

const jobOptions = [
    { value: "Construction", label: "Construction" },
    { value: "Maintenance", label: "Maintenance" },
];

const JobListings = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [jobs, setJobs] = useState([]);
    const [jobSearch, setJobSearch] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState({
        jobClasses: [],
        missing: {
        manHours: false,
        equipment: false,
        materialRequirements: false,
        schedulingNotes: false,
        approved: false,
        },
    });

    const [equipment, setEquipment] = useState([]);
    const [canApprove, setCanApprove] = useState(false);

    // Single merged modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("edit"); // "edit" | "add"

    const [form, setForm] = useState(emptyJob);
    const [toggleMissingFilter, setToggleMissingFilter] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        async function isAuth() {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) navigate("/login");
        }

        const role = Session.getRole();

        if (role === "" || !canNavigate.includes(role)) {
            navigate("/accessdenied");
            return;
        }

        async function fetchCanApprove() {
            const role = Session.getRole();
            if (role === "Project Manager") {
                setCanApprove(true);
                return;
            }
            setCanApprove(false);
        }

        isAuth();
        fetchCanApprove();
    }, [navigate]);

    useEffect(() => {
        const loadAll = async () => {
            try {
                setError("");
                setLoading(true);

                const [jobsData, equipmentData] = await Promise.all([
                    getJobs(),
                    getEquipment(),
                ]);

                const filteredEquipment = (equipmentData || []).filter(
                (e) =>
                    e.isActive !== false &&
                    e.category !== "Vehicle" &&
                    e.category !== "Trailer"
                );

                setEquipment(filteredEquipment);
                setJobs(jobsData || []);
            } catch (e) {
                setError(e?.message || "Failed to load job listings.");
            } finally {
                setLoading(false);
            }
        };

        loadAll();
    }, []);

    const toggleModal = () => setIsModalOpen((v) => !v);

    const openEditModal = (row) => {
        setModalMode("edit");
        setForm({ ...emptyJob, ...row });
        setIsModalOpen(true);
    };

    const openAddModal = () => {
        setModalMode("add");
        setForm({ ...emptyJob });
        setIsModalOpen(true);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");

            const payload = {
                id: form.id,
                isOpen: !!form.isOpen,
                materialRequirements: form.materialRequirements?.trim() || "",
                schedulingNotes: form.schedulingNotes?.trim() || "",
                equipmentIds: form.equipmentIds || [],
                manHours:
                    form.manHours === "" ||
                    form.manHours === null ||
                    form.manHours === undefined
                    ? 0
                    : Number(form.manHours),
                address: form.address,
            };

            payload.customerName = form.customerName?.trim() || "";
            payload.jobName = form.jobName?.trim() || "";
            payload.soldDate = form.soldDate || null;
            payload.rep = form.rep || "";
            payload.jobClass = form.jobClass || "";
            payload.quickBooksJobId = form.quickBooksJobId?.trim() || form.invoiceNumber?.trim() || null;

            if (modalMode === "add") {
                const { id } = await createJob(payload);
                setJobs((prev) => [...prev, { ...payload, id, equipmentNames: [] }]);
            } else {
                await updateJob(form.id, payload);
                // Optimistic update
                setJobs((prev) =>
                    prev.map((j) => {
                    if (j.id !== form.id) return j;

                    const names = (payload.equipmentIds || [])
                        .map((id) => equipment.find((e) => e.id === id)?.equipmentName)
                        .filter(Boolean);

                    return { ...j, ...payload, equipmentNames: names };
                    })
                );
            }

            setIsModalOpen(false);
        } catch (e) {
            setError(e?.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (job) => {
        try {
            setSaving(true);
            setError("");

            const payload = {
                id: job.id,
                approvedOn: new Date().toISOString(),
            };

            await updateJob(job.id, payload);

            setJobs((prev) =>
            prev.map((j) => (j.id === job.id ? { ...j, ...payload } : j))
            );
        } catch (e) {
            setError(e?.message || "Approve failed.");
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleDateString();
    };

    const jobTitle = (row) => {
        const a = row.customerName || "";
        const b = row.jobName || "";
        const title = [a, b].filter(Boolean).join(" - ");
        return title || "-";
    };

    const truncateStyle = {
        maxWidth: 260,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    };

    const filteredJobs = useMemo(() => {
        const term = jobSearch.trim().toLowerCase();

        return (jobs || []).filter((j) => {
            const matchesSearch =
                !term ||
                (j.customerName || "").toLowerCase().includes(term) ||
                (j.jobName || "").toLowerCase().includes(term) ||
                (j.rep || "").toLowerCase().includes(term) ||
                (j.jobClass || "").toLowerCase().includes(term) ||
                (j.materialRequirements || "").toLowerCase().includes(term) ||
                (j.schedulingNotes || "").toLowerCase().includes(term);

            if (!matchesSearch) return false;

            if (filters.jobClasses.length) {
                if (!filters.jobClasses.includes(j.jobClass)) return false;
            }

            if (filters.missing.manHours) {
                const mh = j.manHours;
                if (Number(mh) && Number(mh) > 0) return false;
            }

            if (filters.missing.equipment) {
                const hasEq = Array.isArray(j.equipmentIds) && j.equipmentIds.length > 0;
                if (hasEq) return false;
            }

            if (filters.missing.materialRequirements) {
                if ((j.materialRequirements || "").trim()) return false;
            }

            if (filters.missing.schedulingNotes) {
                if ((j.schedulingNotes || "").trim()) return false;
            }

            if (filters.missing.approved) {
                if (j.approvedOn !== null) return false;
            }

            return true;
        });
    }, [jobs, jobSearch, filters]);

    const equipmentOptions = useMemo(() => {
        return (equipment || []).map((e) => ({
            value: e.id,
            label: e.equipmentName,
        }));
    }, [equipment]);

    const selectedEquipmentOptions = useMemo(() => {
        const ids = form.equipmentIds || [];
        return equipmentOptions.filter((opt) => ids.includes(opt.value));
    }, [form.equipmentIds, equipmentOptions]);

    const crewDaysLabel = useMemo(() => {
        const mh = Number(form.manHours);
        if (!mh || mh <= 0) return "-";
        const crewDays = Math.ceil(mh / 20);
        return `${crewDays} Crew Day${crewDays === 1 ? "" : "s"}`;
    }, [form.manHours]);

    // Derived: does this row already have a QuickBooks invoice number?
    const hasInvoiceNumber = !!(form.quickBooksJobId);

    // ---------------- API CALLS ----------------
    const getJobs = async () => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/GetAll`, {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to load job listings.");
        }

        return await response.json();
    };

    const updateJob = async (id, payload) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/${id}`, {
        method: "PUT",
        headers: !token
            ? { "Content-Type": "application/json" }
            : {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to update job.");
        }
    };

    const createJob = async (payload) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/Create`, {
            method: "POST",
            headers: !token
                ? { "Content-Type": "application/json" }
                : { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to create job.");
        }

        return await response.json(); // { id: newId }
    };

    const removeJob = async (id) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/Remove/${id}`, {
            method: "DELETE",
            headers: !token
                ? { "Content-Type": "application/json" }
                : { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to remove job.");
        }

        const jobsData = await getJobs();

        setJobs(jobsData || []);
        setIsModalOpen(false);
    };

    const getEquipment = async () => {
        const token = Session.getToken();
        const response = await fetch(`api/Equipment/GetAll`, {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to load equipment.");
        }

        return await response.json();
    };

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (filters.jobClasses.length) n++;
        if (filters.missing.equipment) n++;
        if (filters.missing.manHours) n++;
        if (filters.missing.schedulingNotes) n++;
        if (filters.missing.materialRequirements) n++;
        if (filters.missing.approved) n++;
        return n;
    }, [filters]);

    // ---------------- UI ----------------
    return (
        <div>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}

            <Container className="container-main">
                {/* Header */}
                <Row className="align-items-center g-3">
                    <Col>
                        <h3>Job Listings</h3>
                    </Col>

                    <Col xs="12" md="auto">
                        <div className="d-flex gap-3 w-100">
                        <Button
                            color="primary"
                            className="flex-fill flex-md-grow-0 text-nowrap"
                            onClick={openAddModal}
                        >
                            + Add
                        </Button>
                        </div>
                    </Col>
                </Row>

                {loading ? (
                    <div
                        style={{
                        marginTop: "2rem",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        }}
                    >
                        <Spinner />
                    </div>
                ) : (
                <>
                    {/* Search + count */}
                    <Row className="row-20" style={{ marginTop: 10 }}>
                        <Col md="6">
                            <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1.5rem",
                            }}
                            >
                                <Input
                                    type="text"
                                    value={jobSearch}
                                    onChange={(e) => setJobSearch(e.target.value)}
                                    placeholder="Search jobs..."
                                />
                                <Button
                                    color="link"
                                    style={{ padding: 0 }}
                                    onClick={() => setFiltersOpen(true)}
                                >
                                    <div style={{ position: "relative" }}>
                                    <BsFilter size={24} />
                                    {activeFilterCount > 0 && (
                                        <Badge
                                        color="primary"
                                        pill
                                        style={{ position: "absolute", top: -10 }}
                                        >
                                        {activeFilterCount}
                                        </Badge>
                                    )}
                                    </div>
                                </Button>
                            </div>
                        </Col>

                        <Col
                            md="6"
                            className="d-flex justify-content-md-end align-items-center"
                        >
                            <div
                            className="text-muted"
                            style={{ fontSize: 14, alignSelf: "end" }}
                            >
                                Showing {filteredJobs.length} of {jobs.length}
                            </div>
                        </Col>
                    </Row>

                    {/* Jobs table */}
                    <Row className="row-20">
                        <Col>
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                    <th>Open</th>
                                    <th>Invoice Number</th>
                                    <th>Customer Name - Job Name</th>
                                    <th>Approved</th>
                                    <th>Sold Date</th>
                                    <th>Man Hours</th>
                                    <th>Scheduled Hours</th>
                                    <th>Logged Hours</th>
                                    <th>Rep</th>
                                    <th>Job Class</th>
                                    <th>Address</th>
                                    <th>Equipment</th>
                                    <th>Material Requirements</th>
                                    <th>Scheduling Notes</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredJobs.map((row) => {
                                        const isApproved =
                                            !!row.approvedOn || row.approved === true;

                                        return (
                                            <tr
                                            key={row.id}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => openEditModal(row)}
                                            >
                                                <td>
                                                    <Input
                                                    type="checkbox"
                                                    disabled
                                                    checked={row.isOpen}
                                                    name="active"
                                                    />
                                                </td>
                                                <td>{row.quickBooksJobId ?? "-"}</td>
                                                <td style={{ ...truncateStyle, maxWidth: 340 }}>
                                                    {jobTitle(row)}
                                                </td>

                                                <td>
                                                    {isApproved ? (
                                                    formatDate(row.approvedOn)
                                                    ) : canApprove ? (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        disabled={saving}
                                                        onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(row);
                                                        }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    ) : (
                                                    "-"
                                                    )}
                                                </td>

                                                <td>{formatDate(row.soldDate)}</td>
                                                <td>{row.manHours ?? "-"}</td>
                                                <td>{row.scheduledHours ?? "-"}</td>
                                                <td>{row.loggedHours ?? "-"}</td>
                                                <td>{row.rep || "-"}</td>
                                                <td>{row.jobClass || "-"}</td>
                                                <td>{row.address || "-"}</td>

                                                <td style={{ maxWidth: 180 }}>
                                                    {Array.isArray(row.equipmentNames) &&
                                                    row.equipmentNames.length > 0 ? (
                                                    row.equipmentNames.map((name, i) => (
                                                        <div key={i}>{name}</div>
                                                    ))
                                                    ) : row.equipmentIds?.length ? (
                                                    row.equipmentIds.map((id) => {
                                                        const eq = equipment.find((e) => e.id === id);
                                                        return (
                                                        <div key={id}>
                                                            {eq?.equipmentName || "-"}
                                                        </div>
                                                        );
                                                    })
                                                    ) : (
                                                    "-"
                                                    )}
                                                </td>

                                                <td style={truncateStyle}>
                                                    {row.materialRequirements || "-"}
                                                </td>
                                                <td style={truncateStyle}>
                                                    {row.schedulingNotes || "-"}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {filteredJobs.length === 0 && (
                                    <tr>
                                        <td colSpan={11} style={{ textAlign: "center" }}>
                                        No job listings found.
                                        </td>
                                    </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </>
                )}

                {/* Filters Drawer */}
                <Offcanvas
                isOpen={filtersOpen}
                toggle={() => setFiltersOpen(false)}
                direction="end"
                >
                <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
                    Filters
                </OffcanvasHeader>

                <OffcanvasBody>
                    {/* Job Class */}
                    <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Job Class</div>

                    <FormGroup check>
                        <Label check>
                        <Input
                            type="checkbox"
                            checked={filters.jobClasses.includes("Construction")}
                            onChange={(e) => {
                            const checked = e.target.checked;
                            setFilters((prev) => {
                                const set = new Set(prev.jobClasses);
                                checked
                                ? set.add("Construction")
                                : set.delete("Construction");
                                return { ...prev, jobClasses: Array.from(set) };
                            });
                            }}
                        />{" "}
                        Construction
                        </Label>
                    </FormGroup>

                    <FormGroup check className="mt-2">
                        <Label check>
                        <Input
                            type="checkbox"
                            checked={filters.jobClasses.includes("Maintenance")}
                            onChange={(e) => {
                            const checked = e.target.checked;
                            setFilters((prev) => {
                                const set = new Set(prev.jobClasses);
                                checked
                                ? set.add("Maintenance")
                                : set.delete("Maintenance");
                                return { ...prev, jobClasses: Array.from(set) };
                            });
                            }}
                        />{" "}
                        Maintenance
                        </Label>
                    </FormGroup>
                    </div>

                    <hr />

                    {/* Needs Update */}
                    <div style={{ marginBottom: 18 }}>
                    <div
                        style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        }}
                    >
                        <div>
                        <div style={{ fontWeight: 700 }}>Needs update</div>
                        <div
                            className="text-muted"
                            style={{ fontSize: 12, marginTop: 4 }}
                        >
                            Show jobs missing one or more fields.
                        </div>
                        </div>

                        <div
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            setToggleMissingFilter((prev) => !prev);
                        }}
                        >
                        <BsChevronDown
                            style={{
                            transform: toggleMissingFilter ? "rotate(180deg)" : "",
                            }}
                        />
                        </div>
                    </div>

                    {toggleMissingFilter && (
                        <div style={{ marginTop: 14 }}>
                        <FormGroup check>
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.manHours}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    manHours: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Man Hours missing / 0
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.equipment}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    equipment: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            No equipment assigned
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.materialRequirements}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    materialRequirements: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Material requirements empty
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.schedulingNotes}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    schedulingNotes: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Scheduling notes empty
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.approved}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    approved: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Missing Approval
                            </Label>
                        </FormGroup>
                        </div>
                    )}
                    </div>

                    <hr />

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 10 }}>
                    <Button
                        color="secondary"
                        outline
                        onClick={() =>
                        setFilters({
                            jobClasses: [],
                            missing: {
                            manHours: false,
                            equipment: false,
                            materialRequirements: false,
                            schedulingNotes: false,
                            approved: false,
                            },
                        })
                        }
                        style={{ flex: 1 }}
                    >
                        Clear
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => setFiltersOpen(false)}
                        style={{ flex: 1 }}
                    >
                        Done
                    </Button>
                    </div>
                </OffcanvasBody>
                </Offcanvas>

                {/* Merged Add / Edit Modal */}
                <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
                    <ModalHeader toggle={toggleModal}>
                        {modalMode === "add" ? "Add Job Listing" : "Edit Job Listing"}
                    </ModalHeader>

                    <ModalBody>
                        <Form>
                            {/* ── Header: compact read-only title if QB-linked, full fields otherwise ── */}
                            {hasInvoiceNumber ? (
                                /*
                                * This job is already linked to QuickBooks. Show the read-only
                                * Customer - Job Name header; identity fields are owned by QB.
                                */
                                <Row className="align-items-center" style={{ marginBottom: 12 }}>
                                    <Col md={8}>
                                        <div className="text-muted" style={{ fontSize: 14 }}>
                                        <strong>{jobTitle(form)}</strong>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup check style={{ marginTop: 6 }}>
                                        <Label check>
                                            <Input
                                            type="checkbox"
                                            checked={!!form.isOpen}
                                            onChange={(e) =>
                                                handleChange("isOpen", e.target.checked)
                                            }
                                            />{" "}
                                            Is Job Open
                                        </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ) : (
                                /*
                                * No QB invoice number - show all identity fields.
                                * Applies to both add mode AND edit mode for jobs not yet
                                * linked to QuickBooks. Invoice Number field is add-only.
                                */
                                <>
                                    <Row className="align-items-center">
                                        <Col md={5}>
                                            <FormGroup>
                                                <Label for="customerName">Customer Name</Label>
                                                <Input
                                                type="text"
                                                id="customerName"
                                                placeholder="First Last"
                                                value={form.customerName || ""}
                                                onChange={(e) =>
                                                    handleChange("customerName", e.target.value)
                                                }
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={4}>
                                            <FormGroup>
                                                <Label for="soldDate">Sold Date</Label>
                                                <Input
                                                type="date"
                                                id="soldDate"
                                                value={form.soldDate?.slice(0, 10) || ""}
                                                onChange={(e) =>
                                                    handleChange("soldDate", e.target.value)
                                                }
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup check style={{ marginTop: 6 }}>
                                                <Label check>
                                                <Input
                                                    type="checkbox"
                                                    checked={!!form.isOpen}
                                                    onChange={(e) =>
                                                    handleChange("isOpen", e.target.checked)
                                                    }
                                                />{" "}
                                                Is Job Open
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={8}>
                                            <FormGroup>
                                                <Label for="jobName">Job Name</Label>
                                                <Input
                                                type="text"
                                                id="jobName"
                                                placeholder="Job name"
                                                value={form.jobName || ""}
                                                onChange={(e) =>
                                                    handleChange("jobName", e.target.value)
                                                }
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={4}>
                                            <FormGroup>
                                            <Label for="invoiceNumber">Invoice Number</Label>
                                            <Input
                                                type="text"
                                                id="invoiceNumber"
                                                placeholder="e.g. INV-1042"
                                                value={form.invoiceNumber || ""}
                                                onChange={(e) =>
                                                handleChange("invoiceNumber", e.target.value)
                                                }
                                            />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="rep">Rep</Label>
                                                <Select
                                                options={repOptions}
                                                value={
                                                    repOptions.find((o) => o.value === form.rep) ?? null
                                                }
                                                onChange={(selected) =>
                                                    handleChange("rep", selected?.value ?? "")
                                                }
                                                placeholder="Select rep..."
                                                inputId="rep"
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="jobClass">Job Class</Label>
                                                <Select
                                                options={jobOptions}
                                                value={
                                                    jobOptions.find(
                                                    (o) => o.value === form.jobClass
                                                    ) ?? null
                                                }
                                                onChange={(selected) =>
                                                    handleChange("jobClass", selected?.value ?? "")
                                                }
                                                placeholder="Select job class..."
                                                inputId="jobClass"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {/* ── Shared fields (always shown) ── */}
                            <FormGroup>
                                <Label for="materialRequirements">Material Requirements</Label>
                                <Input
                                id="materialRequirements"
                                type="textarea"
                                rows={4}
                                value={form.materialRequirements || ""}
                                onChange={(e) =>
                                    handleChange("materialRequirements", e.target.value)
                                }
                                placeholder="e.g. Two tons weather boulders..."
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="schedulingNotes">Customer Scheduling Notes</Label>
                                <Input
                                id="schedulingNotes"
                                type="textarea"
                                rows={4}
                                value={form.schedulingNotes || ""}
                                onChange={(e) =>
                                    handleChange("schedulingNotes", e.target.value)
                                }
                                placeholder="e.g. Customer requests work after 2pm..."
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="address">Address</Label>
                                <Input
                                id="address"
                                value={form.address || ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Enter the address of the job site..."
                                />
                            </FormGroup>

                            <Row>
                                <Col md={8}>
                                    <FormGroup>
                                        <Label for="equipmentId">Equipment</Label>
                                        <Select
                                        isMulti
                                        options={equipmentOptions}
                                        value={selectedEquipmentOptions}
                                        onChange={(selected) => {
                                            const ids = (selected || []).map((s) => s.value);
                                            handleChange("equipmentIds", ids);
                                        }}
                                        placeholder="Select equipment..."
                                        closeMenuOnSelect={false}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="manHours">Man Hours</Label>
                                        <Input
                                        id="manHours"
                                        type="number"
                                        min="0"
                                        value={form.manHours ?? ""}
                                        onChange={(e) =>
                                            handleChange("manHours", e.target.value)
                                        }
                                        placeholder="0"
                                        />
                                    </FormGroup>

                                    <div style={{ marginTop: -6, fontWeight: 600 }}>
                                        {crewDaysLabel}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        (1 crew day = 20 hours)
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>

                    <ModalFooter className="d-flex justify-content-between">
                        {modalMode === "edit" && !hasInvoiceNumber ? (
                            <Button 
                            color="danger" 
                            outline 
                            onClick={() => removeJob(form.id)}
                            >
                                Remove Job Listing
                            </Button>
                        ) : (
                            /* Spacer so Save/Cancel stay right-aligned in add mode */
                            <div />
                        )}

                        <div className="d-flex gap-2">
                            <Button
                                color="secondary"
                                onClick={toggleModal}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button color="primary" onClick={handleSave} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default JobListings;